import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "appCheckbox", "scopeCheckbox", "redirectUriField", "appTypeRadio"]
  static values = { 
    clientId: String,
    clientSecret: String 
  }

  connect() {
    // Set initial visibility when controller connects
    this.updateRedirectUriVisibility()
  }

  // Copy text to clipboard
  copy(event) {
    const text = event.currentTarget.dataset.text
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopyFeedback(event.currentTarget)
      }).catch(err => {
        this.fallbackCopy(text)
      })
    } else {
      this.fallbackCopy(text)
    }
  }

  // Fallback copy method for older browsers
  fallbackCopy(text) {
    const textArea = document.createElement("textarea")
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }

  // Toggle between truncated and full credential display
  toggleCredential(event) {
    const element = event.currentTarget
    const fullValue = element.dataset.full
    const currentText = element.textContent
    const truncatedValue = this.getTruncatedValue(fullValue)
    
    if (currentText === truncatedValue) {
      // Show full value
      element.textContent = fullValue
      element.style.fontSize = '10px'
      element.title = 'Click to truncate'
    } else {
      // Show truncated value
      element.textContent = truncatedValue
      element.style.fontSize = '12px'
      element.title = 'Click to show full'
    }
  }

  // Helper to create truncated version of a credential
  getTruncatedValue(value) {
    if (value.length <= 20) return value
    return `${value.substring(0, 8)}...${value.substring(value.length - 8)}`
  }

  // Show visual feedback when copying
  showCopyFeedback(button) {
    const originalText = button.innerHTML
    button.innerHTML = '<i class="fa fa-check text-success"></i>'
    button.disabled = true
    
    setTimeout(() => {
      button.innerHTML = originalText
      button.disabled = false
    }, 1000)
  }

  // Handle select all checkbox toggle
  toggleSelectAll() {
    const isChecked = this.selectAllTarget.checked
    
    this.appCheckboxTargets.forEach(checkbox => {
      checkbox.checked = isChecked
    })
  }

  // Handle individual checkbox change
  checkboxChanged() {
    const totalCheckboxes = this.appCheckboxTargets.length
    const checkedCheckboxes = this.appCheckboxTargets.filter(checkbox => checkbox.checked).length
    
    // Update select all checkbox state
    if (checkedCheckboxes === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checkedCheckboxes === totalCheckboxes) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  // Handle scope checkbox dependencies
  scopeChanged(event) {
    const checkbox = event.currentTarget
    const scope = checkbox.value
    
    // Check if this is a write permission
    if (scope.endsWith(':write')) {
      const resource = scope.replace(':write', '')
      const readScope = `${resource}:read`
      
      // Find the corresponding read checkbox
      const readCheckbox = this.scopeCheckboxTargets.find(cb => cb.value === readScope)
      
      if (readCheckbox && checkbox.checked) {
        // When checking write permission, also check read permission
        readCheckbox.checked = true
      }
      // When unchecking write permission, leave read permission as-is
    }
  }

  // Handle app type radio button changes
  appTypeChanged() {
    this.updateRedirectUriVisibility()
  }

  // Show/hide redirect URI field based on app type
  updateRedirectUriVisibility() {
    const selectedType = this.getSelectedAppType()
    
    // Check if target exists (might not be present on show page)
    if (this.hasRedirectUriFieldTarget) {
      const redirectField = this.redirectUriFieldTarget
      
      if (selectedType === 'user') {
        // User apps need redirect URI (Authorization Code flow)
        redirectField.style.display = 'block'
      } else if (selectedType === 'mcp_server') {
        // MCP servers don't need redirect URI (Client Credentials flow)
        redirectField.style.display = 'none'
      }
    }
  }

  // Get the currently selected app type
  getSelectedAppType() {
    if (this.hasAppTypeRadioTarget) {
      const checkedRadio = this.appTypeRadioTargets.find(radio => radio.checked)
      return checkedRadio ? checkedRadio.value : 'user'
    }
    return 'user'
  }
}